import nativeBridge from '@ifit/native-webview-interface';
import React, { useCallback, useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useInfiniteAchievementsQuery } from '../../../../client/query/achievements';
import { useMembership } from '../../../../client/query/users';
import { IEventOrChallengeOrMilestone } from '@ifit/gateway-wolf-athenaeum-service';
import { ThemeContext } from '@ifit/themes/ThemeContext';
import {
  discoverChallenges,
  myChallengesTitle,
  noChallengesAchievementData,
  upgradeFreeChallengesAchievementCaption,
} from '../../../../i18n/translations';
import Loading from '@ifit/mp-loading';
import Badge from '@ifit/mp-badge';
import Icon from '@ifit/react-icons/dist/components/icon';
import LOCKED_LINE from '@ifit/react-icons/dist/icons/LOCKED_LINE';
import InfoPanel from '@ifit/mp-info-panel';
import useInfoPanelPosition from '../../../../helpers/use-info-panel-positioning';
import { challengesInfoPanel } from '../../../../i18n/translations';
import { shark, solidWhite } from '../../../../theme';
import styles from './index.module.scss';
import InfiniteScrollRQ from '../../../../components/infinite-scroll-rq';
import GridSection from '../../../../components/grid-section';
import { useConfig } from '../../../../store/reducers/config';

const Challenges: React.FC = () => {
  const { isFreeLibrary } = useMembership();
  const {
    dataMemo: { data: content = [], hasMoreItems },
    query: { isLoading, isFetching },
    fetchNextPageCallback,
  } = useInfiniteAchievementsQuery(IEventOrChallengeOrMilestone.Challenge, 30);
  const { language } = useConfig();
  const callChallenges = () => {
    return (event: React.MouseEvent) => {
      nativeBridge.callNative({
        methodName: 'RunBehavior',
        key: 'NavigateToPath',
        path: 'library?page=challenges',
        respond: false,
      });
      event.preventDefault();
    };
  };

  const { theme } = useContext(ThemeContext);
  const iconColor = {
    LIGHT: shark,
    DARK: solidWhite,
    VAULT: shark,
  }[theme];

  const mapContent = useCallback(
    badgeContent =>
      badgeContent?.flatMap(b =>
        b?.challenges?.map(ch => (
          <Badge
            data={{
              achievementId: ch.achievementId,
              completed: ch.completed,
              createdDate: ch.createdDate,
              completedDate: ch.completedDate,
              completedWorkouts: ch.completedWorkouts,
              description: ch.description,
              badgeImage: ch.badgeImage,
              title: ch.title,
              hasClaimedReward: ch.hasClaimedReward,
              totalWorkouts: ch.totalWorkouts,
              redemptionLink: ch.redemptionLink,
              remainingWorkouts: ch.remainingWorkouts,
            }}
            key={ch.challengeId}
            language={language}
            referralSource={'Library'}
          ></Badge>
        )),
      ),
    [language],
  );

  const badges = useMemo(() => mapContent(content), [content, mapContent]);

  const infoPanelPosition = useInfoPanelPosition();

  if (isFreeLibrary) {
    return (
      <div>
        <header>
          <div>
            <h1>{myChallengesTitle()}</h1>
            <InfoPanel
              info={challengesInfoPanel()}
              className={styles.infoPanel}
              verticalPosition={infoPanelPosition}
            ></InfoPanel>
          </div>
        </header>
        <div className="free-user">
          <Icon
            icon={LOCKED_LINE}
            color={iconColor}
            size={32}
            className="locked-line"
          />
          <p>{upgradeFreeChallengesAchievementCaption()}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <header>
        <div>
          <h1>{myChallengesTitle()}</h1>
          <InfoPanel
            info={challengesInfoPanel()}
            className={styles.infoPanel}
            verticalPosition={infoPanelPosition}
          ></InfoPanel>
        </div>
        <Link className="header-link" to="" onClick={callChallenges()}>
          {discoverChallenges()}
        </Link>
      </header>
      {isLoading && <Loading absolute />}
      {!isLoading && !badges?.length && (
        <p className="no-data">{noChallengesAchievementData()}</p>
      )}
      <InfiniteScrollRQ
        hasMoreItems={hasMoreItems}
        loadMore={fetchNextPageCallback}
        isFetching={isFetching}
      >
        <GridSection libraryBadges={badges} containsLibraryBadges />
      </InfiniteScrollRQ>
    </div>
  );
};

export default Challenges;
